import { formatDisplayDateTime } from "../../utils/dateUtils";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { ServerDeletedFilterValue } from "../../shared/ServerListFilters";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import ServerStatusPills from "../../shared/ServerStatusPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { newNetwork, Datum } from "../../interface/NewNetwork";
import { NewNetworkAxiosInstance } from "../../Network/NewNetworkAxiosInstance";
import { ScrollArea } from "../../components/ui/scroll-area";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import ListOfFacilityName from "../Shared/ListOfProviderName";
import { Option } from "../../interface/Option";
import FileUpload from "Pages/uploadFile/Network/Network";
import SpecialtyProviderSelector from "Pages/Shared/ProviderSpecialty";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";
import useQaAutomationIds from "../../utils/useQaAutomationIds";


// Linked record status does not change the parent record status.
const linkedInactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  if (item.__inactive === true || item.is_deleted === true) return true;
  const state = item.is_active ?? item.Is_active ?? item.active ?? item.status ?? item.Status;
  return state === false || state === 0 || state === "0" ||
    ["false", "inactive", "disabled"].includes(String(state ?? "").trim().toLowerCase());
};
const responseRows = (response: any): any[] => {
  const raw = response?.data?.result ?? response?.data?.data ?? response?.data?.rows ?? response?.data;
  if (Array.isArray(raw)) return raw;
  for (const key of ["result", "data", "rows"]) if (Array.isArray(raw?.[key])) return raw[key];
  return [];
};
const fetchStatusCatalogue = async (client: any, endpoint: string): Promise<any[]> => {
  const result = new Map<string, any>();
  for (const active of ["true", "false"]) {
    for (let page = 1; page <= 100; page++) {
      const response = await client.get(endpoint, { params: { page, limit: 100, active, is_deleted: "false" } });
      const rows = responseRows(response);
      for (const item of rows) if (item?.id != null) result.set(String(item.id), item);
      const total = Number(response?.data?.pagination?.totalPages ?? response?.data?.totalPages);
      if (rows.length === 0 || (Number.isFinite(total) && total > 0 ? page >= total : rows.length < 100)) break;
    }
  }
  return Array.from(result.values());
};

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

type NetworkProviderDisplay = {
  providerDbId?: number;
  Provider_id: string;
  Provider_name: string;
  __inactive?: boolean;
  is_active?: boolean | string;
  status?: string;
};

const normalizeNetworkProviders = (raw: any): NetworkProviderDisplay[] => {
  const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw];

  return list.flatMap((entry: any): NetworkProviderDisplay[] => {
    if (entry == null) return [];

    if (typeof entry === "number" || typeof entry === "string") {
      const rawId = String(entry).trim();
      if (!rawId) return [];

      const numericId = Number(rawId);
      const providerDbId = Number.isFinite(numericId) ? numericId : undefined;

      return [{
        providerDbId,
        Provider_id: rawId,
        Provider_name: providerDbId != null ? `Provider #${providerDbId}` : rawId,
      }];
    }

    const dbIdRaw =
      entry.providerDbId ??
      entry.dbId ??
      entry.provider_db_id ??
      entry.providerDbID ??
      entry.provider?.id ??
      entry.Provider?.id ??
      entry.id;
    const numericDbId = Number(dbIdRaw);
    const providerDbId = Number.isFinite(numericDbId) ? numericDbId : undefined;

    const businessId =
      entry.Provider_id ??
      entry.provider_id ??
      entry.providerId ??
      entry.provider?.Provider_id ??
      entry.provider?.provider_id ??
      entry.Provider?.Provider_id ??
      entry.Provider?.provider_id ??
      (providerDbId != null ? String(providerDbId) : "");

    const providerName =
      entry.Provider_name ??
      entry.provider_name ??
      entry.providerName ??
      entry.provider?.Provider_name ??
      entry.provider?.provider_name ??
      entry.provider?.name ??
      entry.Provider?.Provider_name ??
      entry.Provider?.provider_name ??
      entry.Provider?.name ??
      entry.name ??
      (providerDbId != null ? `Provider #${providerDbId}` : String(businessId || ""));

    return [{
      providerDbId,
      Provider_id: String(businessId ?? ""),
      Provider_name: String(providerName ?? ""),
      is_active: entry.is_active ?? entry.provider?.is_active ?? entry.Provider?.is_active,
      status: entry.status ?? entry.provider?.status ?? entry.Provider?.status,
      __inactive: linkedInactive(entry) || linkedInactive(entry.provider) || linkedInactive(entry.Provider),
    }];
  });
};

const normalizeNetworkRow = (item: any): Datum => {
  const rawStatus = item?.status ?? item?.Status;

  return {
    ...item,
    id: item?.id,
    network_name:
      item?.network_name ??
      item?.Network_name ??
      item?.name ??
      "",
    provider: normalizeNetworkProviders(
      item?.provider ?? item?.providers ?? item?.facilities,
    ),
    status:
      rawStatus === true ||
      rawStatus === 1 ||
      rawStatus === "1" ||
      rawStatus === "true" ||
      rawStatus === "Active" ||
      rawStatus === "active"
        ? "Active"
        : "Inactive",
    description: item?.description ?? "",
    is_deleted: Boolean(
      item?.is_deleted ?? item?.Is_deleted ?? item?.deleted ?? false,
    ),
    created_by:
      item?.created_by ?? item?.Created_by ?? item?.createdBy ?? null,
    createdat:
      item?.createdat ?? item?.created_at ?? item?.createdAt ?? null,
    updated_by:
      item?.updated_by ?? item?.Updated_by ?? item?.updatedBy ?? [],
    updatedat:
      item?.updatedat ?? item?.updated_at ?? item?.updatedAt ?? null,
  } as Datum;
};

const getSelectedProviderDbIds = (options: any[]): string[] => {
  const ids = options
    .map((option: any) => {
      const rawId =
        option?.providerDbId ??
        option?.dbId ??
        option?.provider_db_id ??
        option?.providerDbID ??
        option?.value?.id ??
        option?.value;

      const numericId = Number(rawId);
      return Number.isFinite(numericId) ? String(numericId) : null;
    })
    .filter((id): id is string => Boolean(id));

  return Array.from(new Set(ids));
};

const getNetworkProviderDbIds = (providers: any[]): string[] => {
  const ids = providers
    .map((provider: any) => {
      if (typeof provider === "number") return String(provider);
      if (typeof provider === "string" && /^\d+$/.test(provider.trim())) {
        return String(Number(provider.trim()));
      }

      const rawId =
        provider?.providerDbId ??
        provider?.provider_db_id ??
        provider?.providerDbID ??
        provider?.id;

      const numericId = Number(rawId);
      return Number.isFinite(numericId) ? String(numericId) : null;
    })
    .filter((id): id is string => Boolean(id));

  return Array.from(new Set(ids));
};

// Strict display-name validation used by Network Name and the embedded Provider Name.
// Every word must start with a capital letter. Hyphenated words are supported,
// e.g. "Al Noor Hospital" and "King-Fahad Clinic".
const ENTITY_NAME_PATTERN =
  /^[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*(?:\s+[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*)*$/;

const ENTITY_NAME_FORMAT_ERROR =
  "Invalid name. Only letters are allowed and each word must start with a capital letter.";

const validateEntityName = (
  value: string,
  requiredMessage: string,
  minimumMessage: string,
): string => {
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage;
  if (trimmed.length < 3) return minimumMessage;
  if (!ENTITY_NAME_PATTERN.test(trimmed)) return ENTITY_NAME_FORMAT_ERROR;
  return "";
};

const NewNetwork: React.FC = () => {
  useQaAutomationIds("network");
  const [items, setItems] = useState<newNetwork>({ result: [] });
  const [providerCatalogue, setProviderCatalogue] = useState<any[]>([]);
  const refreshProviderCatalogue = async (): Promise<any[]> => {
    try {
      const rows = await fetchStatusCatalogue(providerAxiosInstance, "getProvider");
      setProviderCatalogue(rows);
      return rows;
    } catch (error) {
      console.warn("Could not refresh Provider statuses", error);
      return providerCatalogue;
    }
  };
  useEffect(() => { void refreshProviderCatalogue(); }, []);
  const linkedProviders = (raw: any, catalogue: any[] = providerCatalogue): NetworkProviderDisplay[] =>
    normalizeNetworkProviders(raw).map(provider => {
      const match = catalogue.find(row =>
        (provider.providerDbId != null && String(row.id) === String(provider.providerDbId)) ||
        (provider.Provider_id && String(row.provider_id ?? row.Provider_id ?? "") === provider.Provider_id)
      );
      return match ? { ...provider, is_active: match.is_active ?? match.active, status: match.status ?? match.Status, __inactive: linkedInactive(match) } : provider;
    });

  const [provider, setProvider] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [description, setDescription] = useState<string>("");
  const [Name, setName] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearProvider, setclearProvider] = useState(false);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);

  const [selectedProviders, setSelectedProviders] = useState<NetworkProviderDisplay[]>([]);
  const [selectedProviderNetworkId, setSelectedProviderNetworkId] = useState<string | number | null>(null);
  const token = localStorage.getItem("token") || "";
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const [Encounter, setEncounter] = useState<"OP" | "IP" | "Both">("OP");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [advancedSearch, setAdvancedSearch] = useState({ search: "", status: "" });
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");

  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);

  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);

  const fetchNetworkDetails = async (networkId: string | number, fallback?: Datum): Promise<Datum> => {
    const response = await NewNetworkAxiosInstance.get(`getNetwork/${networkId}`);
    const rawResult =
      response.data?.result ??
      response.data?.network ??
      response.data?.data ??
      response.data;
    const rawNetwork = Array.isArray(rawResult) ? rawResult[0] : rawResult;

    if (!rawNetwork) {
      throw new Error("Network details were not returned by the backend.");
    }

    const mergedNetwork = {
      ...(fallback || {}),
      ...rawNetwork,
      status: rawNetwork?.status ?? rawNetwork?.Status ?? (fallback as any)?.status,
      is_deleted: rawNetwork?.is_deleted ?? (fallback as any)?.is_deleted,
      created_by: rawNetwork?.created_by ?? (fallback as any)?.created_by,
      createdat: rawNetwork?.createdat ?? rawNetwork?.created_at ?? (fallback as any)?.createdat,
      updated_by: rawNetwork?.updated_by ?? (fallback as any)?.updated_by,
      updatedat: rawNetwork?.updatedat ?? rawNetwork?.updated_at ?? (fallback as any)?.updatedat,
    };

    return normalizeNetworkRow(mergedNetwork);
  };

  useEffect(() => { fetchUsers(); }, []);

  const clear = () => {
    setName("");
    setDescription("");
    setProvider([]);
    setIsActive(true);

    // Reset all Network form validation when the dialog closes/reopens.
    setNameError("");
    setProvidersError("");
    setGloableError("");
  };

  const handleOpenDialog = (item: Datum) => {
    setSelectedItem(item);
    if (dialogRef.current) (dialogRef.current as any).showModal();
  };
  const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  const handleViewClick = async (item: Datum) => {
    setShowAllUpdates(false);
    try {
      const [details, catalogue] = await Promise.all([fetchNetworkDetails(item.id, item), refreshProviderCatalogue()]);
      setSelectedItem({ ...details, provider: linkedProviders((details as any).provider, catalogue) } as Datum);
    } catch (error) {
      console.error("Failed to load network details:", error);
      setSelectedItem(normalizeNetworkRow(item));
      toast({
        title: "Warning",
        description: "Network details could not be refreshed. Showing the available row data.",
      });
    }
  };

  const handleEditClick = async (item: Datum) => {
    try {
      const [details, catalogue] = await Promise.all([fetchNetworkDetails(item.id, item), refreshProviderCatalogue()]);
      await handleEdit({ ...details, provider: linkedProviders((details as any).provider, catalogue) } as Datum);
    } catch (error) {
      console.error("Failed to load network details for edit:", error);
      handleEdit(normalizeNetworkRow(item));
    }
    setIsDialogOpen(true);
  };

  const handleFacilityChange = (selectedOptions: Option[]) => {
    const locked = provider.filter((item: any) => linkedInactive(item) && !selectedOptions.some(next => String(next.value) === String(item.value)));
    setProvider([...selectedOptions, ...locked]);
  };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, {
    search: string;
    status: string;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { ...advancedSearch, deleted: filterDeleted },
    fetchPage: ({ page, limit, filters }) =>
      NewNetworkAxiosInstance.get("getNetwork", {
        params: {
          page,
          limit,
          active:
            filters.status === "Active"
              ? "true"
              : filters.status === "Inactive"
                ? "false"
                : undefined,
          is_deleted: filters.deleted,
        },
      }),
    setRows: (rows: Datum[]) => setItems({ result: rows }),
    normalizeRow: (item: any) => normalizeNetworkRow(item),
    sortRows: (rows: Datum[]) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    isClientFallbackActive: (filters) =>
      filters.search.trim().length > 0,
    clientFilterRows: (rows: Datum[], filters) => {
      const search = filters.search.toLowerCase().trim();
      if (!search) return rows;

      return rows.filter((row) =>
        String(row.id ?? "").toLowerCase().includes(search) ||
        String(row.network_name ?? "").toLowerCase().includes(search) ||
        row.provider?.some((provider) =>
          String(provider.Provider_id ?? "").toLowerCase().includes(search) ||
          String(provider.Provider_name ?? "").toLowerCase().includes(search)
        )
      );
    },
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({ title: "Error", description: "Failed to fetch Network group", variant: "destructive" });
    },
  });

  const toggleNetworkStatus = async (item: Datum, force = false): Promise<void> => {
    try {
      const currentStatus = item.status === "Active" || item.status === true;
      const currentProviders = Array.isArray((item as any).provider) ? (item as any).provider : [];
      const providerIds = getNetworkProviderDbIds(currentProviders);

      if (currentProviders.length > 0 && providerIds.length !== currentProviders.length) {
        toast({
          title: "Error",
          description: "Provider relation IDs are missing. Please reopen the network and reselect the providers.",
          variant: "destructive",
        });
        return;
      }

      const updatedItem = {
        token,
        force,
        data: {
          Network_name: item.network_name,
          provider: providerIds,
          Status: !currentStatus ? "Active" : "Inactive",
          id: item.id,
        },
      };
      const response = await NewNetworkAxiosInstance.put("updateNetwork", updatedItem);
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceError("");
        toast({ title: "Success", description: `Status changed to ${!currentStatus ? "Active" : "Inactive"}.`, variant: "success" });
      } else {
        setForceId(item.id);
        setForceAction("status");
        setForceError(response.data.error || "This network is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const [nameError, setNameError] = useState<string>("");
  const [providersError, setProvidersError] = useState<string>("");
  const [gloableError, setGloableError] = useState<string>("");

  const normalizeNetworkUniqueValue = (value: any): string =>
    String(value ?? "").trim().toLowerCase();

  const extractNetworkRows = (response: any): any[] => {
    const raw =
      response?.data?.result ??
      response?.data?.data ??
      response?.data?.networks ??
      [];

    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.rows)) return raw.rows;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.result)) return raw.result;

    return [];
  };

  const extractNetworkTotalCount = (
    response: any,
    fallbackCount: number,
  ): number => {
    const raw =
      response?.data?.totalCount ??
      response?.data?.pagination?.totalCount ??
      response?.data?.pagination?.total ??
      response?.data?.count;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallbackCount;
  };

  // Fetch all active/non-deleted Network rows so duplicate validation is
  // independent of the table's current pagination page.
  const fetchAllNetworks = async (): Promise<Datum[]> => {
    const limit = 100;
    let page = 1;
    let rows: Datum[] = [];
    let totalCount = 0;

    do {
      const response = await NewNetworkAxiosInstance.get("getNetwork", {
        params: {
          page,
          limit,
          is_deleted: "false",
        },
      });

      const pageRows = extractNetworkRows(response).map((row: any) =>
        normalizeNetworkRow(row),
      );

      rows = [...rows, ...pageRows];
      totalCount = extractNetworkTotalCount(response, rows.length);

      if (pageRows.length === 0) break;
      if (pageRows.length < limit && rows.length >= totalCount) break;

      page += 1;
    } while (rows.length < totalCount);

    return rows;
  };

  const isDuplicateNetworkName = async (
    value: string,
    excludeId: string | number | null = null,
  ): Promise<boolean> => {
    const normalizedName = normalizeNetworkUniqueValue(value);
    if (!normalizedName) return false;

    const rows = await fetchAllNetworks();

    return rows.some((item) =>
      (excludeId === null || Number(item.id) !== Number(excludeId)) &&
      !item.is_deleted &&
      normalizeNetworkUniqueValue(item.network_name) === normalizedName
    );
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setGloableError("");

    const validationError = validateEntityName(
      value,
      "Network Name is required.",
      "Network Name must be at least 3 characters long.",
    );

    if (validationError) {
      setNameError(validationError);
      return;
    }

    // Duplicate validation is performed by the debounced effect below
    // against all backend pages, not only items.result/current page.
    setNameError("");
  };

  useEffect(() => {
    // A closed form must never keep or receive a stale validation message.
    if (!isDialogOpen) {
      setNameError("");
      return;
    }

    const value = Name.trim();

    if (
      validateEntityName(
        value,
        "Network Name is required.",
        "Network Name must be at least 3 characters long.",
      )
    ) return;

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      try {
        const duplicate = await isDuplicateNetworkName(
          value,
          editIndex === null ? null : editIndex,
        );

        if (cancelled) return;

        // Ignore stale responses if the user typed something else meanwhile.
        if (normalizeNetworkUniqueValue(Name) !== normalizeNetworkUniqueValue(value)) {
          return;
        }

        setNameError(duplicate ? "This Network Name already exists." : "");
      } catch (error) {
        console.error("Failed to validate Network Name uniqueness:", error);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [Name, editIndex, isDialogOpen]);

  const [isPatDialogOpen, setIsPatDialogOpen] = useState(false);
  const [Name2, setName2] = useState<string>("");
  const [ProviderID, setProviderID] = useState<string>("");
  const [Speciality, setSpeciality] = useState<string>("");
  const [isActive2, setIsActive2] = useState<boolean>(true);
  const [name2Error, setName2Error] = useState<string>("");
  const [providerError, setProviderError] = useState<string>("");
  const [specialityError, setSpecialityError] = useState<string>("");
  const [clearSpecialty, setClearSpecialty] = useState(false);
  const [specialty, setSpecialty] = useState<Option[]>([]);

  const handleSpecialtyChange = (specialties: Option[]) => { setSpecialty(specialties); };

  const handleName2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName2(value);
    setName2Error(
      validateEntityName(
        value,
        "Provider Name is required.",
        "Name must be at least 3 characters long",
      ),
    );
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProviderID(value);
    setProviderError("");
    if (!value.trim()) return;
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    if (!alphanumericRegex.test(value)) { setProviderError("Provider ID must contain only letters and numbers"); return; }
  };

  // Validate against all Provider records, not only the current Network or visible page.
  const validateNewNetworkProvider = async (name: string, businessId: string): Promise<string | null> => {
    const normalize = (value: any) => String(value ?? "").trim().toLowerCase();
    const pageLimit = 100;
    let page = 1;

    while (true) {
      const response = await providerAxiosInstance.get("getProvider", {
        params: { page, limit: pageLimit, is_deleted: "all" },
      });
      const raw = response.data?.result ?? response.data?.data ?? response.data?.providers ?? [];
      const rows: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.rows) ? raw.rows
        : Array.isArray(raw?.data) ? raw.data
        : Array.isArray(raw?.result) ? raw.result
        : [];

      if (rows.some((item) => normalize(item.Provider_name ?? item.provider_name ?? item.providerName ?? item.name) === normalize(name))) {
        return "name";
      }
      if (rows.some((item) => normalize(item.Provider_id ?? item.provider_id ?? item.providerId) === normalize(businessId))) {
        return "id";
      }

      const totalRaw = response.data?.totalCount ?? response.data?.pagination?.totalCount ?? response.data?.pagination?.total ?? response.data?.count;
      const total = totalRaw == null ? NaN : Number(totalRaw);
      if (Number.isFinite(total)) {
        if (page * pageLimit >= total || rows.length === 0) break;
      } else if (rows.length < pageLimit) {
        break;
      }
      if (page >= 1000) throw new Error("Provider duplicate validation exceeded the maximum page count.");
      page += 1;
    }
    return null;
  };

  // Live duplicate validation for Provider Name / Provider ID inside Network.
  // Runs shortly after typing stops, so the user sees duplicate errors before pressing Add.
  useEffect(() => {
    if (!isPatDialogOpen) return;

    const providerName = Name2.trim();
    const providerBusinessId = ProviderID.trim();

    // Only check values that already pass the basic local validation.
    const validName = !validateEntityName(
      providerName,
      "Provider Name is required.",
      "Name must be at least 3 characters long",
    );
    const validId = providerBusinessId.length > 0 && /^[a-zA-Z0-9]+$/.test(providerBusinessId);

    if (!validName && !validId) return;

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      try {
        const duplicate = await validateNewNetworkProvider(
          validName ? providerName : "__skip_name__",
          validId ? providerBusinessId : "__skip_id__",
        );

        if (cancelled) return;

        // Ignore stale responses if the user changed the value while the request was running.
        if (Name2.trim() !== providerName || ProviderID.trim() !== providerBusinessId) return;

        if (validName) {
          setName2Error(duplicate === "name" ? "This Provider Name already exists." : "");
        }

        if (validId) {
          setProviderError(duplicate === "id" ? "This Provider ID already exists." : "");
        }
      } catch (error) {
        console.error("Failed to validate Provider duplicate while typing:", error);
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [Name2, ProviderID, isPatDialogOpen]);

  const addItem2 = async (): Promise<void> => {
    try {
      setName2Error(""); setProviderError(""); setSpecialityError("");
      const providerNameValidationError = validateEntityName(
        Name2,
        "Provider Name is required.",
        "Name must be at least 3 characters long",
      );
      if (providerNameValidationError) {
        setName2Error(providerNameValidationError);
        toast({ title: "Error", description: providerNameValidationError, variant: "destructive" });
        return;
      }
      const finalProviderID = ProviderID.trim();
      if (!finalProviderID) { setProviderError("Provider ID is required."); toast({ title: "Error", description: "Provider ID is required.", variant: "destructive" }); return; }
      if (!/^[a-zA-Z0-9]+$/.test(finalProviderID)) { setProviderError("Provider ID must contain only letters and numbers."); toast({ title: "Error", description: "Invalid Provider ID.", variant: "destructive" }); return; }
      if (specialty.length === 0) { setSpecialityError("At least one Specialty must be selected."); toast({ title: "Error", description: "At least one Specialty must be selected.", variant: "destructive" }); return; }
      const duplicate = await validateNewNetworkProvider(Name2.trim(), finalProviderID);
      if (duplicate === "name") { setName2Error("This Provider Name already exists."); toast({ title: "Duplicate Provider", description: "This Provider Name already exists.", variant: "destructive" }); return; }
      if (duplicate === "id") { setProviderError("This Provider ID already exists."); toast({ title: "Duplicate Provider", description: "This Provider ID already exists.", variant: "destructive" }); return; }
      const ValuesSpecialty = specialty.map((option) => option.label);
      const newGroupArray = [{ Provider_name: Name2, Provider_id: finalProviderID, Speciality: ValuesSpecialty, Status: isActive2 ? "Active" : "Inactive", Encounter_checks: Encounter }];
      const payload = { data: newGroupArray, token };
      const response = await providerAxiosInstance.post("addNewProvider", payload);
      if (response.data.success) {
        fetchItems();

        const rawCreated =
          response.data?.result ??
          response.data?.results?.[0] ??
          response.data?.provider ??
          response.data?.data?.[0];
        const createdProvider = Array.isArray(rawCreated) ? rawCreated[0] : rawCreated;

        let providerDbId = Number(createdProvider?.id);
        let providerBusinessId = String(
          createdProvider?.Provider_id ??
          createdProvider?.provider_id ??
          finalProviderID ??
          "",
        );
        let providerName = String(
          createdProvider?.provider_name ??
          createdProvider?.Provider_name ??
          Name2.trim(),
        );

        if (!Number.isFinite(providerDbId)) {
          try {
            const lookup = await providerAxiosInstance.get("getProvider", {
              params: { name: Name2.trim(), active: true },
            });
            const lookupRows = lookup.data?.result || [];
            const matched = lookupRows.find((row: any) => {
              const rowBusinessId = String(
                row?.Provider_id ?? row?.provider_id ?? row?.providerId ?? "",
              );
              const rowName = String(
                row?.provider_name ?? row?.Provider_name ?? row?.providerName ?? "",
              );
              return (
                (providerBusinessId && rowBusinessId === providerBusinessId) ||
                rowName.toLowerCase() === providerName.toLowerCase()
              );
            });

            if (matched) {
              providerDbId = Number(matched.id);
              providerBusinessId = String(
                matched.Provider_id ?? matched.provider_id ?? providerBusinessId,
              );
              providerName = String(
                matched.provider_name ?? matched.Provider_name ?? providerName,
              );
            }
          } catch (lookupError) {
            console.error("Failed to resolve newly created provider ID:", lookupError);
          }
        }

        if (Number.isFinite(providerDbId)) {
          const newOption: any = {
            value: String(providerDbId),
            label: `(${providerBusinessId || providerDbId}) ${providerName}`,
            providerDbId,
            dbId: providerDbId,
            providerId: providerBusinessId || String(providerDbId),
            providerName,
          };

          setProvider((prev) => {
            if ((prev as any[]).some((p: any) => Number(p.providerDbId) === providerDbId)) return prev;
            return [...prev, newOption];
          });
        } else {
          toast({
            title: "Provider Added",
            description: "Provider was created, but its database ID was not returned. Please search and select it before saving the network.",
          });
        }

        setName2(""); setProviderID(""); setSpecialty([]); setIsActive2(true);
        setIsPatDialogOpen(false);
        toast({ title: "Success", description: "New Provider Added Successfully." });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || error.message || "Failed to validate or add provider.", variant: "destructive" });
    }
  };

  const addItem = async (): Promise<void> => {
    setProvidersError("");
    setGloableError("");

    const value = Name.trim();
    let localNameError = "";

    localNameError = validateEntityName(
      value,
      "Network Name is required.",
      "Network Name must be at least 3 characters long.",
    );

    if (localNameError) setNameError(localNameError);
    if (provider.length === 0) {
      setProvidersError("At least one Provider must be selected.");
    }

    if (localNameError || provider.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Final backend-wide duplicate guard in addition to live validation.
      if (await isDuplicateNetworkName(value)) {
        const message = "This Network Name already exists.";
        setNameError(message);
        toast({
          title: "Duplicate Network Name",
          description: message,
          variant: "destructive",
        });
        return;
      }

      const providerIds = getSelectedProviderDbIds(provider as any[]);

      if (providerIds.length !== provider.length) {
        setProvidersError("Please reselect the provider(s) so the Network can use their database relation IDs.");
        toast({
          title: "Validation Error",
          description: "One or more selected providers are missing the database relation ID. Please reselect them.",
          variant: "destructive",
        });
        return;
      }

      const newGroupArray = [{ Network_name: Name, provider: providerIds, Status: isActive ? "Active" : "Inactive" }];
      const payload = { data: newGroupArray, token };
      const response = await NewNetworkAxiosInstance.post("addNewNetwork", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "New Network Group Added Successfully." });
      } else {
        const messages = Array.isArray(response.data?.errors)
          ? response.data.errors.map((err: any) => err?.error || err?.message).filter(Boolean)
          : [];

        const message =
          messages[0] ||
          response.data?.error ||
          response.data?.message ||
          "Failed to add the group.";

        if (String(message).toLowerCase().includes("exist") || String(message).toLowerCase().includes("duplicate")) {
          setNameError("This Network Name already exists.");
        }

        toast({ title: "Error", description: message, variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An unexpected error occurred.";

      if (String(message).toLowerCase().includes("exist") || String(message).toLowerCase().includes("duplicate")) {
        setNameError("This Network Name already exists.");
      }

      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  const resolveProviderDbIdForEdit = async (
    providerItem: NetworkProviderDisplay,
  ): Promise<NetworkProviderDisplay> => {
    if (Number.isFinite(Number(providerItem.providerDbId))) {
      return providerItem;
    }

    try {
      const response = await providerAxiosInstance.get("getProvider", {
        params: {
          page: 1,
          limit: 100,
          name: providerItem.Provider_name?.trim() || undefined,
          active: "true",
          is_deleted: "false",
        },
      });

      const rawResult =
        response.data?.result ??
        response.data?.data ??
        [];

      const rows = Array.isArray(rawResult)
        ? rawResult
        : Array.isArray(rawResult?.data)
          ? rawResult.data
          : [];

      const expectedBusinessId = String(providerItem.Provider_id ?? "").trim();
      const expectedName = String(providerItem.Provider_name ?? "")
        .trim()
        .toLowerCase();

      const matched = rows.find((row: any) => {
        const rowBusinessId = String(
          row?.Provider_id ??
          row?.provider_id ??
          row?.providerId ??
          ""
        ).trim();

        const rowName = String(
          row?.provider_name ??
          row?.Provider_name ??
          row?.providerName ??
          row?.name ??
          ""
        )
          .trim()
          .toLowerCase();

        return (
          (expectedBusinessId && rowBusinessId === expectedBusinessId) ||
          (expectedName && rowName === expectedName)
        );
      });

      const resolvedId = Number(matched?.id);

      if (Number.isFinite(resolvedId)) {
        return {
          ...providerItem,
          providerDbId: resolvedId,
        };
      }
    } catch (error) {
      console.error(
        `Failed to resolve DB id for provider "${providerItem.Provider_name}":`,
        error,
      );
    }

    return providerItem;
  };

  const handleEdit = async (item: Datum) => {
    const normalized = normalizeNetworkRow(item);
    setName(normalized.network_name);

    const normalizedProviders = Array.isArray((normalized as any).provider)
      ? ((normalized as any).provider as NetworkProviderDisplay[])
      : [];

    const resolvedProviders = await Promise.all(
      normalizedProviders.map(resolveProviderDbIdForEdit),
    );

    const facilitiesGroupData = resolvedProviders.map((obj) => ({
      value: String(obj.providerDbId ?? obj.Provider_id ?? ""),
      label: `(${obj.Provider_id || obj.providerDbId || ""}) ${obj.Provider_name}`,
      providerDbId: obj.providerDbId,
      dbId: obj.providerDbId,
      providerId: obj.Provider_id,
      providerName: obj.Provider_name,
      __inactive: linkedInactive(linkedProviders([obj])[0]),
      is_active: linkedProviders([obj])[0]?.is_active,
      status: linkedProviders([obj])[0]?.status,
    } as any));

    setProvider(facilitiesGroupData);
    setIsActive(normalized.status === "Active" || normalized.status === true);
    setEditIndex(normalized.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    setProvidersError("");
    setGloableError("");

    const value = Name.trim();
    let localNameError = "";

    localNameError = validateEntityName(
      value,
      "Network Name is required.",
      "Network Name must be at least 3 characters long.",
    );

    if (localNameError) setNameError(localNameError);
    if (provider.length === 0) {
      setProvidersError("At least one Provider must be selected.");
    }

    if (localNameError || provider.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Exclude the record currently being edited from duplicate comparison.
      if (await isDuplicateNetworkName(value, id)) {
        const message = "This Network Name already exists.";
        setNameError(message);
        toast({
          title: "Duplicate Network Name",
          description: message,
          variant: "destructive",
        });
        return;
      }

      const providerIds = getSelectedProviderDbIds(provider as any[]);

      if (providerIds.length !== provider.length) {
        setProvidersError("Please reselect the provider(s) so the Network can use their database relation IDs.");
        toast({
          title: "Validation Error",
          description: "One or more selected providers are missing the database relation ID. Please reselect them.",
          variant: "destructive",
        });
        return;
      }

      const updatedItem = { token, force: false, data: { Network_name: Name, provider: providerIds, Status: isActive ? "Active" : "Inactive", id } };
      const response = await NewNetworkAxiosInstance.put("updateNetwork", updatedItem);
      if (response.data.success) {
        await fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Network group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Datum) => {
    const rows = ["name;provider;active"];
    const Provider = item.provider || [];
    const maxLen = Math.max(Provider.length);
    for (let index = 0; index < Math.max(1, maxLen); index++) {
      const provider = index < Provider.length ? Provider[index].Provider_name || "" : "";
      rows.push([item.network_name || "", provider, item.status ? "Active" : "Inactive"].join(";"));
    }
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Network${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await NewNetworkAxiosInstance.delete(`softDeleteNetwork/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Network Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This network is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Network";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await NewNetworkAxiosInstance.patch(`restoreNetwork/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Network restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore Network", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore Network", variant: "destructive" });
    }
  };

  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteNetwork/${id}?force=true` : `deleteNetwork/${id}`;
      const response = await NewNetworkAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Network Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This network is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete Network");
    }
  };

  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await NewNetworkAxiosInstance.delete(`softDeleteNetwork/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Network force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete Network");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete Network");
    }
  };

  const removeProviderFromNetwork = async (
    networkId: string | number,
    providerDbId: number | undefined,
    providerName: string,
  ): Promise<void> => {
    try {
      if (!Number.isFinite(Number(providerDbId))) {
        toast({
          title: "Error",
          description: "The provider relation ID is missing. Refresh the Network list and try again.",
          variant: "destructive",
        });
        return;
      }

      const currentNetwork = items.result.find((item) => item.id === networkId);
      if (!currentNetwork) return;

      const currentProviders = Array.isArray((currentNetwork as any).provider)
        ? ((currentNetwork as any).provider as NetworkProviderDisplay[])
        : [];

      const updatedProviders = currentProviders.filter(
        (providerItem) => Number(providerItem.providerDbId) !== Number(providerDbId),
      );

      if (updatedProviders.length === 0) {
        toast({
          title: "Error",
          description: "Cannot remove the last provider. Network must have at least one provider.",
          variant: "destructive",
        });
        return;
      }

      const providerIds = getNetworkProviderDbIds(updatedProviders);
      if (providerIds.length !== updatedProviders.length) {
        toast({
          title: "Error",
          description: "One or more provider relation IDs are missing. Refresh the Network list and try again.",
          variant: "destructive",
        });
        return;
      }

      const updatedItem = {
        token,
        force: false,
        data: {
          Network_name: currentNetwork.network_name,
          provider: providerIds,
          Status: currentNetwork.status,
          id: networkId,
        },
      };

      const response = await NewNetworkAxiosInstance.put("updateNetwork", updatedItem);
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: `Provider "${providerName}" removed from network successfully.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to remove provider.", variant: "destructive" });
    }
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <span className="block text-center font-medium">{currentPage * pageSize + row.index + 1}</span>,
    },
    {
      accessorKey: "network_name",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Network Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = row.original.is_deleted;
        return (
          <span className={`block text-center font-medium ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("network_name")}
          </span>
        );
      },
    },
    {
      accessorKey: "provider",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Provider{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const providers = linkedProviders(row.getValue("provider"));
        const networkId = row.original.id;
        if (!providers || !Array.isArray(providers)) return <span className="text-gray-400 text-center block">-</span>;
        const searchTerm = advancedSearch.search.toLowerCase().trim();
        const hasSearch = searchTerm.length > 0;
        const filteredProviders = hasSearch ? providers.filter(p => p.Provider_name?.toLowerCase().includes(searchTerm)) : providers;
        const displayProviders = hasSearch && filteredProviders.length > 0 ? filteredProviders.slice(0, 2) : providers.slice(0, 2);
        const remainingCount = hasSearch ? filteredProviders.length - 2 : providers.length - 2;
        return (
          <div className="flex flex-col items-center gap-1">
            <div className="flex flex-wrap justify-center items-center gap-1.5">
              {displayProviders.map((provider, idx) => {
                const isMatching = hasSearch && provider.Provider_name?.toLowerCase().includes(searchTerm);
                return (
                  <div key={idx} className={`inline-flex items-center gap-1 rounded-full px-2 py-1 transition-all ${linkedInactive(provider) ? 'bg-gray-200 border border-gray-300' : isMatching ? 'bg-yellow-100 border-2 border-yellow-500 shadow-md' : 'bg-blue-50'}`}>
                    <span className={`text-xs font-medium ${linkedInactive(provider) ? 'text-gray-600' : isMatching ? 'text-yellow-900 font-bold' : 'text-blue-800'}`}>
                      {isMatching && <i className="fa-solid fa-star text-yellow-600 mr-1"></i>}
                      {provider.Provider_name}{linkedInactive(provider) ? " (Inactive)" : ""}
                    </span>
                    {!linkedInactive(provider) && <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${provider.Provider_name}" from this network?`)) { removeProviderFromNetwork(networkId, provider.providerDbId, provider.Provider_name); } }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-5 h-5 flex items-center justify-center transition-all" title="Remove from network">
                      <i className="fa-solid fa-times text-xs"></i>
                    </button>}
                  </div>
                );
              })}
              {remainingCount > 0 && (
                <button onClick={() => { setSelectedProviders(hasSearch ? filteredProviders : providers); setSelectedProviderNetworkId(networkId); setProviderDialogOpen(true); }}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition-colors ${hasSearch ? 'bg-yellow-600 text-white hover:bg-yellow-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                  +{remainingCount} more {hasSearch && 'matching'}
                </button>
              )}
            </div>
            {hasSearch && filteredProviders.length > 0 && (
              <span className="text-xs text-yellow-700 font-medium mt-1">
                <i className="fa-solid fa-filter mr-1"></i>
                Showing {filteredProviders.length} of {providers.length} providers
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isActive = item.status === "Active" || item.status === true;
        const isDeleted = item.is_deleted;
        const statusValueStr = isActive ? "active" : "inactive";
        return (
          <div className="flex justify-center">
            <button
              data-testid={`network-btn-table-status-toggle-${item.id}`}
              data-current-status={statusValueStr}
              onClick={() => !isDeleted && toggleNetworkStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : isActive ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <Button variant="ghost" className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white">View</Button>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex justify-center items-center gap-2">
            <button
              data-testid={`network-btn-view-details-${item.id}`}
              onClick={() => void handleViewClick(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="network">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900" data-testid="network-page-title">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-network-wired"></i>
          </span>
          Network
        </h5>
      </div>

      <DataTable2
        qaIdPrefix="network-data-table"
        columns={columns}
        data={items.result}
        searchValue={advancedSearch.search}
        onSearchChange={(value) => setAdvancedSearch((previous) => ({ ...previous, search: value }))}
        searchPlaceholder="Search..."
        searchHelpText="Network Name, Provider ID, or Provider Name."
        addButtonLabel="Add New Network"
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <ServerStatusPills
              value={
                advancedSearch.status === "Active"
                  ? "true"
                  : advancedSearch.status === "Inactive"
                    ? "false"
                    : ""
              }
              onChange={(value) => {
                setAdvancedSearch((previous) => ({
                  ...previous,
                  status: value === "true" ? "Active" : value === "false" ? "Inactive" : "",
                }));
                setCurrentPage(0);
              }}
            />
            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />

      {/* View Dialog */}
      {Boolean(selectedItem) && selectedItem && (
        <Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
          <DialogContent
            data-testid="network-dialog-view-details"
            className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
            onInteractOutside={(event: any) => {
              const target = event.target as HTMLElement;
              if (
                target.closest(".list-of-provider-name-select__menu") ||
                target.closest(".list-of-provider-name-select__menu-portal")
              ) {
                event.preventDefault();
              }
            }}
            onPointerDownOutside={(event: any) => {
              const target = event.target as HTMLElement;
              if (
                target.closest(".list-of-provider-name-select__menu") ||
                target.closest(".list-of-provider-name-select__menu-portal")
              ) {
                event.preventDefault();
              }
            }}
            style={{
              boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
              overflow: "visible",
            }}
          >
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
              <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                  <i className="fa-solid fa-network-wired" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2, flexWrap: "wrap" }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>{selectedItem.network_name}</h2>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: (selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: (selectedItem.status === "Active" || selectedItem.status === true) ? "#86efac" : "#fca5a5", border: `1px solid ${(selectedItem.status === "Active" || selectedItem.status === true) ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                      {selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive"}
                    </span>
                    {(selectedItem as any).is_deleted && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                        <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Network ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                </div>
              </div>
            </div>

            {/* Body */}
            <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
              <div style={{ padding: "20px 28px 16px" }}>
                {/* Basic Info */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                    <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: "#3b82f6" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                  {[
                    { label: "Network name", value: selectedItem.network_name, icon: "fa-network-wired", color: "#3b82f6" },
                    { label: "Status", value: selectedItem.status === "Active" || selectedItem.status === true ? "Active" : "Inactive", icon: "fa-toggle-on", color: "#10b981" },
                  ].map((field) => (
                    <div key={field.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, background: `${field.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className={`fa-solid ${field.icon}`} style={{ fontSize: 10, color: field.color }} />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize", letterSpacing: "0.05em" }}>{field.label}</span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{field.value || "—"}</span>
                    </div>
                  ))}
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

                {/* Providers */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                    <i className="fa-solid fa-hospital" style={{ fontSize: 12, color: "#3b82f6" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Assigned providers</span>
                  <span style={{ fontSize: 10, padding: "2px 10px", borderRadius: 9999, background: "#3b82f6", color: "#fff", fontWeight: 700 }}>
                    {selectedItem.provider?.length || 0}
                  </span>
                </div>
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  {selectedItem.provider && selectedItem.provider.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, maxHeight: 220, overflowY: "auto" }}>
                      {linkedProviders(selectedItem.provider).map((p, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: linkedInactive(p) ? "#e5e7eb" : "#f8fafc", borderRadius: 8, border: linkedInactive(p) ? "1px solid #9ca3af" : "1px solid #e2e8f0" }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fa-solid fa-user-doctor" style={{ fontSize: 11, color: "#3b82f6" }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{p.Provider_name}{linkedInactive(p) ? " (Inactive)" : ""}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No providers assigned</span>
                  )}
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 0 20px" }} />

                {/* Audit Trail */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created by</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                        {String(resolveUserName((selectedItem as any).created_by) ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created on</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}
                      </span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}
                      </span>
                    </div>
                  </div>
                </div>
                {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
                  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
                        const updateDate = entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined);
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                                {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
                              {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
                            </div>
                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                              {updateDate ? formatDisplayDateTime(updateDate) : "—"}
                            </span>
                          </div>
                        );
                      })}
                      {selectedItem.updated_by.length > 4 && (
                        <div style={{ textAlign: "center", padding: "6px 0" }}>
                          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
                            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ paddingBottom: 8 }} />
              </div>
            </ScrollArea>

            {/* Footer */}
            <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0 }}>
              <Button data-testid="network-btn-close-details" onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                Close Details
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* All Providers Dialog */}
      <Dialog open={providerDialogOpen} onOpenChange={setProviderDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list-check"></i>All Providers
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2">
                {selectedProviders && linkedProviders(selectedProviders).map((provider, index) => (
                  <div key={index} className={`flex items-center gap-3 p-3 rounded-lg shadow-sm border ${linkedInactive(provider) ? "bg-gray-200 border-gray-300" : "bg-white border-blue-100"}`}>
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0">
                      <i className="fa-solid fa-building text-sm"></i>
                    </div>
                    <span className="text-gray-800 font-medium flex-1">{provider.Provider_name}{linkedInactive(provider) ? " (Inactive)" : ""}</span>
                    {!linkedInactive(provider) && <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${provider.Provider_name}" from this network?`)) { if (selectedProviderNetworkId != null) { removeProviderFromNetwork(selectedProviderNetworkId, provider.providerDbId, provider.Provider_name); setProviderDialogOpen(false); } } }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-8 h-8 flex items-center justify-center transition-all" title="Remove from network">
                      <i className="fa-solid fa-trash-alt text-sm"></i>
                    </button>}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-blue-200">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-blue-600"></i>
                  <span>Total: <strong className="text-blue-900">{selectedProviders?.length || 0}</strong> providers</span>
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setProviderDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">
              <i className="fa-solid fa-check mr-2"></i>Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Network Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);

          if (!open) {
            // X button / outside click / Esc: fully reset the form and validation.
            clear();
            setEditIndex(null);
          }
        }}
      >
        <DialogContent
          data-testid={editIndex === null ? "network-dialog-add-network" : "network-dialog-edit-network"}
          className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}
        >
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
              </div>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add Network" : "Edit Network"}</h2>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
            <div style={{ padding: "22px 28px 32px" }}>
              {gloableError && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 18 }}>
                  <i className="fa-solid fa-circle-exclamation" style={{ color: "#dc2626", marginTop: 2, fontSize: 12 }} />
                  <p style={{ fontSize: 13, color: "#991b1b", margin: 0 }}>{gloableError}</p>
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                  <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
                <Label htmlFor="name" className="text-xs font-semibold text-slate-500">Network Name <span className="text-orange-500" aria-label="required">*</span></Label>
                <Input
                  data-testid="network-input-network-name"
                  id="name"
                  value={Name}
                  onChange={handleNameChange}
                  placeholder="Name is required"
                  className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                />
                {nameError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}

                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                  <Label className="text-xs font-semibold text-slate-500">Status</Label>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
                    <button
                      data-testid="network-btn-network-status-active"
                      type="button"
                      onClick={() => setIsActive(true)}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive ? "#f0fdf4" : "#fff", color: isActive ? "#15803d" : "#64748b" }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
                    </button>
                    <button type="button" onClick={() => setIsActive(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive ? "#fef2f2" : "#fff", color: !isActive ? "#b91c1c" : "#64748b" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                  <i className="fa-solid fa-hospital" style={{ fontSize: 11, color: "#9333ea" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Providers</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${providersError ? "#fca5a5" : "#e2e8f0"}`, padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <Label className="text-xs font-semibold text-slate-500">Assigned Providers <span className="text-orange-500" aria-label="required">*</span></Label>
                <div data-testid="network-select-assigned-providers-wrapper" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                  <div style={{ flex: 1 }}><ListOfFacilityName onChange={handleFacilityChange} defaultValue={provider} clear={clearProvider} /></div>
                  <button
                    data-testid="network-btn-open-add-provider-dialog"
                    type="button"
                    onClick={() => setIsPatDialogOpen(true)}
                    style={{ padding: "8px 14px", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <i className="fa-solid fa-plus mr-1" /> New
                  </button>
                </div>
                {providersError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{providersError}</p>}
              </div>
            </div>
          </ScrollArea>

          <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
            <Button data-testid="network-btn-cancel-network" variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
            <Button
              data-testid={editIndex === null ? "network-btn-add-network-submit" : "network-btn-update-network-submit"}
              disabled={Boolean(validateEntityName(Name, "Network Name is required.", "Network Name must be at least 3 characters long."))}
              onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }}
              style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
            >
              <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
              {editIndex === null ? "Add Network" : "Update Network"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Provider Inside Network Dialog */}
      <Dialog open={isPatDialogOpen} onOpenChange={setIsPatDialogOpen}>
        <DialogContent
          data-testid="network-dialog-add-provider-inside-network"
          className="max-w-[680px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)", overflow: "visible" }}
        >
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                <i className="fa-solid fa-user-plus" />
              </div>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>Add Provider</h2>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
            <div style={{ padding: "22px 28px 32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                  <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="provider_name" className="text-xs font-semibold text-slate-500">Provider Name <span className="text-orange-500" aria-label="required">*</span></Label>
                    <Input
                      data-testid="network-input-provider-name"
                      id="provider_name"
                      value={Name2}
                      onChange={handleName2Change}
                      placeholder="Name is required"
                      className={`mt-1.5 ${name2Error ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {name2Error && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{name2Error}</p>}
                  </div>
                  <div>
                    <Label htmlFor="provider_id" className="text-xs font-semibold text-slate-500">Provider ID <span className="text-orange-500" aria-label="required">*</span></Label>
                    <Input
                      data-testid="network-input-provider-id"
                      id="provider_id"
                      value={ProviderID}
                      onChange={handleIdChange}
                      placeholder="Provider ID is required"
                      className={`mt-1.5 ${providerError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    />
                    {providerError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{providerError}</p>}
                  </div>
                </div>

                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                  <Label className="text-xs font-semibold text-slate-500">Status</Label>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
                    <button
                      data-testid="network-btn-provider-status-active"
                      type="button"
                      onClick={() => setIsActive2(true)}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive2 ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive2 ? "#f0fdf4" : "#fff", color: isActive2 ? "#15803d" : "#64748b" }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
                    </button>
                    <button type="button" onClick={() => setIsActive2(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive2 ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive2 ? "#fef2f2" : "#fff", color: !isActive2 ? "#b91c1c" : "#64748b" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                  <i className="fa-solid fa-list-check" style={{ fontSize: 11, color: "#9333ea" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Specialty</span>
              </div>
              <div data-testid="network-select-provider-specialties-wrapper" style={{ background: "#fff", borderRadius: 12, border: `1px solid ${specialityError ? "#fca5a5" : "#e2e8f0"}`, padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
                <Label className="text-xs font-semibold text-slate-500">Select Specialties <span className="text-orange-500" aria-label="required">*</span></Label>
                <div className="mt-1.5"><SpecialtyProviderSelector onChange={handleSpecialtyChange} clear={clearSpecialty} value={specialty} /></div>
                {specialityError && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{specialityError}</p>}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                  <i className="fa-solid fa-sliders" style={{ fontSize: 11, color: "#16a34a" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Encounter</span>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <Label className="text-xs font-semibold text-slate-500">Encounter Type</Label>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {(["OP", "IP", "Both"] as const).map((val) => (
                    <button
                      data-testid={val === "OP" ? "network-btn-provider-encounter-op" : undefined}
                      type="button"
                      key={val}
                      onClick={() => setEncounter(val)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: Encounter === val ? "1px solid #2563eb" : "1px solid #e2e8f0",
                        background: Encounter === val ? "#eff6ff" : "#fff",
                        color: Encounter === val ? "#1d4ed8" : "#64748b",
                      }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
            <Button variant="outline" onClick={() => setIsPatDialogOpen(false)} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
            <Button
              data-testid="network-btn-save-provider"
              disabled={
                Boolean(validateEntityName(Name2, "Provider Name is required.", "Name must be at least 3 characters long")) ||
                !ProviderID.trim() ||
                !/^[a-zA-Z0-9]+$/.test(ProviderID.trim())
              }
              onClick={() => addItem2()}
              style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
            >
              <i className="fa-solid fa-plus mr-2"></i>Save Provider
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Force / Hard Delete Dialog */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the network.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Network Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status") {
                          const item = items.result.find(i => i.id === forceId);
                          if (item) toggleNetworkStatus(item, true);
                        }
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default NewNetwork;